import React, { Fragment, useState, useEffect, useRef } from "react";
import {
  Card,
  CardTitle,
  Row,
  Col,
  FormGroup,
  Input,
  Table,
  Badge,
  Button,
} from "reactstrap";
import API from "../../redux/actions/API";
import { authHeader } from "../../utils/functions/CoreFunctions";
import Swal from "sweetalert2";
import {
  amountFormat,
  getAlertToast,
  getCurrentAgencyId,
  dateTimeFormat,
} from "../../utils/functions/CustomFunctions";
import { GET_WEBFORM_LIST } from "../../utils/APIEndpoints";
import CustomViewButton from "../common/CustomViewButton";
import CustomTableLoader from "../common/CustomTableLoader";
import CustomTableEmptyRecord from "../common/CustomTableEmptyRecord";
import CustomTableHeader from "../common/CustomTableHeader";
import { Link } from "react-router-dom";
import { debounce } from "underscore";
import Pagination from "react-js-pagination";

const paymentStatusColor = {
  Success: "primary",
  Failed: "danger",
  Pending: "warning",
};

const tableColumns = [
  { title: "Document Name" },
  { title: "Document URL" },
  { title: "Total Fee" },
  { title: "Document Type" },
  { title: "Payment Type" },
  { title: "User Count" },
  { title: "Actions" },
];

function Reports() {
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [gridData, setGridData] = useState([]);
  const [paymentDetailsModal, setPaymentDetailsModal] = useState(false);
  const [feeDetails, setFeeDetails] = useState({});
  const [filter, setFilter] = useState({
    serviceName: "",
    serviceDomainURL: "",
    count: 10,
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  //filter toogle
  const filterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  //Onchange filter
  const changeFilterField = ({ target: { name, value } }) => {
    setFilter({ ...filter, [name]: value });
  };

  const handleReset = () => {
    setFilter((val) => ({
      ...val,
      serviceName: "",
      serviceDomainURL: "",
    }));
  };

  const getGridData = async (queryParams) => {
    setLoading(true);
    const options = {
      method: "GET",
      url: GET_WEBFORM_LIST,
      headers: authHeader(),
      params: queryParams,
    };
    try {
      const response = await API(options);
      const { status, data, totalCount } = response.data;
      if (status === "Success") {
        setTotalRecords(totalCount);
        setGridData(data);
      }
    } catch (error) {
      if (error.response) {
        let { data } = error.response;
        Swal.fire(getAlertToast("error", data.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRowPerPage = ({ target: { value, name } }) => {
    setCurrentPage(0);
    setFilter((val) => ({ ...val, [name]: value || undefined }));
  };

  //debounce
  const delayedQuery = useRef(
    debounce(
      (filter) =>
        getGridData({
          ...filter,
          // count: rowsPerPage || undefined,
          offset: currentPage,
          agencyId: parseInt(getCurrentAgencyId()),
        }),
      500
    )
  ).current;

  useEffect(() => {
    delayedQuery(filter);
  }, [filter]);

  const onPageChanged = (page) => {
    setCurrentPage(page - 1);
    getGridData({
      ...filter,
      // count: rowsPerPage || undefined,
      offset: page - 1,
      agencyId: parseInt(getCurrentAgencyId()),
    });
  };
  useEffect(() => {
    getGridData({
      ...filter,
      // count: rowsPerPage || undefined,
      offset: currentPage,
      agencyId: parseInt(getCurrentAgencyId()),
    });
  }, [rowsPerPage]);

  const { serviceName, serviceDomainURL } = filter;

  return (
    <Fragment>
      <Card body className="mt-5">
        <div>
          <CardTitle className="mb-4 tableTitleBg">WebForm Reports</CardTitle>
          <i
            className="fas fa-filter"
            style={{
              float: "right",
              margin: "-32px 7px 3px 16px",
              fontSize: "19px",
              cursor: "pointer",
            }}
            onClick={filterToggle}
          ></i>
        </div>
        {isFilterOpen && (
          <Row form className="mb-4">
            <Col md={3}>
              <FormGroup>
                <Input
                  type="text"
                  name="serviceName"
                  placeholder="Document Name"
                  onChange={changeFilterField}
                  value={serviceName}
                />
              </FormGroup>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Input
                  type="text"
                  name="serviceDomainURL"
                  placeholder="Document URL"
                  onChange={changeFilterField}
                  value={serviceDomainURL}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <div style={{ marginTop: "4px" }}>
                <Button color="danger" onClick={handleReset}>
                  Reset
                </Button>
              </div>
            </Col>
          </Row>
        )}

        <Table responsive hover>
          {/* Table Header */}
          <CustomTableHeader tableColumns={tableColumns} />
          {/* Table Header */}
          <tbody>
            {loading ? (
              // Table Loader
              <CustomTableLoader columnsCount={tableColumns.length} />
            ) : (
              // Table Loader
              <Fragment>
                {!gridData.length ? (
                  //  Empty Record
                  <CustomTableEmptyRecord columnsCount={tableColumns.length} />
                ) : (
                  //  Empty Record
                  <Fragment>
                    {gridData.map((record, key) => (
                      <tr key={`record_key_${key}`}>
                        <td>{record.serviceName}</td>
                        <td>
                          <a
                            target="_blank"
                            href={
                              window.location.origin +
                              "/preview-document/" +
                              record.serviceDomainURL
                            }
                          >
                            {record.serviceDomainURL}
                          </a>
                        </td>
                        <td>{record.serviceAmount}</td>
                        <td>{record.documentType}</td>
                        <td>{record.paymentType}</td>
                        <td>{record.userCount}</td>
                        <td>
                          {/* Custom View Button  */}
                          <Link
                            to={`/report/webform/${record.agencyServiceId}`}
                          >
                            <CustomViewButton keyvalue={key} />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                )}
              </Fragment>
            )}
          </tbody>
          {/* Table Body */}
        </Table>
        {gridData.length ? (
          <div className="d-flex py-4 justify-content-end space_between">
            <div className="space_between">
              <div>
                <p className="per_page">Records/Page</p>
              </div>
              <div>
                <FormGroup>
                  <Input
                    className="pagi_drop"
                    type="select"
                    name="count"
                    onChange={handleRowPerPage}
                    value={filter.count}
                  >
                    <option value={""}>Show All</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Input>
                </FormGroup>
              </div>
            </div>
            {filter.count && (
              <div>
                <Pagination
                  itemClass="page-item"
                  linkClass="page-link"
                  activePage={currentPage + 1}
                  itemsCountPerPage={filter.count}
                  totalItemsCount={totalRecords}
                  onChange={onPageChanged}
                />
              </div>
            )}
          </div>
        ) : (
          ""
        )}
      </Card>
    </Fragment>
  );
}

export default Reports;
